import { Goal } from 'lucide-react';
import { ReactNode, Suspense, useMemo, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import {
  buildWeeklyPaceSummary,
  WEEKLY_PACE_GOAL,
  WeeklyPaceColumn,
} from '../../../businessLogicService/weeklyPaceColumns';
import { getWeeklySnapshotInfo } from '../../../graphQLOperations/Dashboard';
import {
  DashboardGetWeeklySnapshotQuery,
  WeeklyPaceSkillSource,
} from '../../../graphQLOperations/__generated__/DashboardGetWeeklySnapshotQuery.graphql';
import GoalMet from '../GoalMet/GoalMet';
import { StudentsModal } from '../StudentsModal';
import { FilterOptionValues } from '../TeacherDashboard';
import WeeklyHistory from '../WeeklyHistory/WeeklyHistory';
import { WeeklySnapshotQueryFilters } from '../WeeklySnapshot';
import { WeeklyPaceChart } from './WeeklyPaceChart';

interface Props {
  queryReference: PreloadedQuery<DashboardGetWeeklySnapshotQuery, Record<string, unknown>>;
  queryFilters: WeeklySnapshotQueryFilters;
  filterOption: FilterOptionValues;
  source: WeeklyPaceSkillSource;
  /** Source toggle and info icon, owned by the card so they survive a source change. */
  headerActions: ReactNode;
}

// Sits under the counter while it reads "0 of N": the counter alone says how many students got
// there without ever saying where "there" is, which is the one thing worth repeating when the answer
// is nobody.
const WeeklyPaceGoalPill = (): JSX.Element => (
  <span className="tw-whitespace-nowrap tw-rounded-full tw-border-2 tw-border-solid tw-border-amber-200 tw-bg-amber-100 tw-px-3 tw-py-1 tw-font-atkinson tw-text-xs tw-font-bold tw-text-neutral-950">
    Goal = {WEEKLY_PACE_GOAL}+ skills/wk
  </span>
);

// Same badge/title/subtitle shape and copy as TeacherDashboardWidgetEmpty's Weekly Pace entry, inline
// so the card keeps its own header, week range and toggle rather than being replaced wholesale. The
// `!tw-rotate-34` those copies carry is dropped: no `rotate` scale is configured, so it generates
// nothing and the icon was always upright anyway.
const WeeklyPaceEmptyState = (): JSX.Element => (
  <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6">
    <span className="tw-flex tw-rounded-full tw-bg-stone-50 tw-p-4">
      <Goal size={30} className="tw-text-stone-500" />
    </span>
    <p className="tw-mb-2 tw-font-atkinson tw-text-2xl tw-font-bold tw-text-gray-600">No pace data yet</p>
    <p className="tw-m-0 tw-font-atkinson tw-text-sm tw-text-gray-500">
      Data will appear as students complete skill challenges
    </p>
  </div>
);

export const WeeklyPaceContent = ({
  queryReference,
  queryFilters,
  filterOption,
  source,
  headerActions,
}: Props): JSX.Element => {
  const data = usePreloadedQuery<DashboardGetWeeklySnapshotQuery>(getWeeklySnapshotInfo, queryReference);
  const [selectedColumn, setSelectedColumn] = useState<WeeklyPaceColumn | null>(null);

  const summary = useMemo(
    () => buildWeeklyPaceSummary(data.studentsSkillAssessmentsWeeklySnapshot),
    [data.studentsSkillAssessmentsWeeklySnapshot],
  );

  // Nothing attempted: the body is the empty state and the history link. The counter would only ever
  // read "0 of N", and the source toggle and info icon qualify figures that are not on screen, so
  // they go with the chart they describe rather than hovering over an empty card. The link names the
  // whole dialog here, since with no current week to compare against "past weeks" has no anchor.
  if (!summary.hasAnyActivity) {
    return (
      <>
        <WeeklyPaceEmptyState />
        <div className="tw-flex tw-justify-end">
          <WeeklyHistory variant="link" label="View weekly history" queryFilters={queryFilters} source={source} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-6">
        {/* Same inset as WeeklyPaceChart, so the goal count and the info icon line up with the outer bars. */}
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-4">
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
            <GoalMet
              studentsMetGoal={summary.studentsMetGoal}
              totalStudents={summary.totalStudents}
              queryFilters={queryFilters}
              source={source}
            />
            {summary.studentsMetGoal === 0 && <WeeklyPaceGoalPill />}
          </div>
          {headerActions}
        </div>

        <WeeklyPaceChart summary={summary} onSelectColumn={setSelectedColumn} />

        {selectedColumn && (
          <Suspense>
            <StudentsModal
              filterOption={filterOption}
              visible
              setModalVisible={() => setSelectedColumn(null)}
              header="Weekly Snapshot"
              label={selectedColumn.label}
              score={selectedColumn.category}
              includeNoAttempts={selectedColumn.includesNoAttempts}
              source={source}
              type="week"
            />
          </Suspense>
        )}
      </div>

      <div className="tw-flex tw-justify-end">
        <WeeklyHistory variant="link" queryFilters={queryFilters} source={source} />
      </div>
    </>
  );
};
